import { Breadcrumb, Layout, Typography } from 'antd';
import cn from 'classnames';
import { useLayoutEffect } from 'react';

import { Error403Unauthorized } from '@app/components/Error403Unauthorized';
import type { NPage } from '@app/components/Page/types/Page.main';
import { NavButtons } from '@app/components/Page/ui/NavButtons';
import { usePageContext } from '@app/providers/PageProvider/usePageContext';

import '@app/components/Page/styles/Page.desktop.scss';
import '@app/components/Page/styles/Page.mobile.scss';

const { Header, Content } = Layout;
const { Title } = Typography;

export const Page: NPage.Main = ({
  children,
  headerContent,
  headerNavigationContent,
  secondLevelData,
  thirdLevelData,
  disableBreadcrumbs = false,
  goBackRoute,
  contentLayoutClassName = '',
  hideBackButton,
  disableContentStyles = false,
  mobileTitle,
}) => {
  const {
    isSmall,
    showPage,
    menuSidebarState,
    menuStateButtonClickHandler,
    goBackHandler,
    breadcrumbsItems,
    pageTitle,
    colorBgContainer,
    isMobile,
    titleData,
    pageTitleDataTestId,
    isPageWithExtraTitle,
    contentStyle,
    isCalendarPage,
    setOptions,
  } = usePageContext();

  const headerNavigationNode = headerNavigationContent ?? (
    <NavButtons
      isMobile={isMobile}
      hideBackButton={hideBackButton}
      onBackClick={goBackHandler}
      onMenuClick={menuStateButtonClickHandler}
      menuSidebarState={menuSidebarState}
      mobileTitle={mobileTitle}
    />
  );

  useLayoutEffect(() => {
    setOptions({
      headerContent,
      secondLevelData,
      thirdLevelData,
      disableBreadcrumbs,
      goBackRoute,
      contentLayoutClassName,
      hideBackButton,
      disableContentStyles,
    });
  }, [
    headerContent,
    secondLevelData,
    thirdLevelData,
    disableBreadcrumbs,
    goBackRoute,
    contentLayoutClassName,
    hideBackButton,
    setOptions,
    disableContentStyles,
  ]);

  return (
    <Layout
      className={cn(`page-content-container ${contentLayoutClassName}`, {
        isClosed: isSmall,
      })}
    >
      <Header className="header" style={{ background: colorBgContainer }}>
        {!disableBreadcrumbs ? (
          <>
            <div className="toggler-and-breadcrumbs">
              {headerNavigationNode}
              {Boolean(pageTitle && !disableBreadcrumbs) && (
                <div className="navigation">
                  <Title
                    className="title"
                    level={3}
                    data-testid={pageTitleDataTestId}
                  >
                    {isPageWithExtraTitle ? titleData : pageTitle}
                  </Title>
                  <Breadcrumb items={breadcrumbsItems} />
                </div>
              )}
            </div>
            {Boolean(pageTitle && !disableBreadcrumbs) && (
              <div className="header-breadcrumbs-mobile">
                <Breadcrumb items={breadcrumbsItems} />
              </div>
            )}
            <div className="custom-part">{headerContent}</div>
          </>
        ) : (
          <>
            {headerNavigationNode}

            {headerContent}
          </>
        )}
      </Header>

      <Content
        className={`content ${!isCalendarPage ? 'page-content' : ''}`}
        style={contentStyle}
      >
        {showPage ? children : <Error403Unauthorized />}
      </Content>
    </Layout>
  );
};
